// @flow
import * as React from 'react';
import {
  CircularProgress,
  IconButton,
  Stack,
  Tab as MuiTab,
  Tabs
} from '@mui/material';
import { useMemo, useState } from 'react';
import { usePlanContracts } from './hooks/use-plan-contracts';
import { Check, X } from '@phosphor-icons/react';
import {
  DataGrid,
  DataGridColumn,
  ListPageWrapper,
  PageTitle,
  useSessionStorageState
} from '../../shared';
import { ParentCompanyFilter } from './ui/parent-company-filter';
import _ from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { mainBackendApolloClient } from '@coverright/data-access/apollo-clients';
import { useSaveEnabledMaContractMutation, useSaveEnabledPdpContractMutation } from '@coverright/data-access/medicare';
import { PageInput } from '@coverright/data-access/types/enrollment';
import { SearchInput } from '@coverright/ui/inputs';


export const PlanCoverage = () => {
  const [tab, setTab] = useState(0);
  const type = tab ? 'pdp' : 'ma';
  const {contracts, loading} = usePlanContracts(type)
  const [page, setPage] = usePage(type);
  const [term, setTerm] = useTerm(type);

  const [parentCompany, setParentCompany] = useParentCompany(type)
  const parentCompanies = _.uniq(contracts.map(c => c.parentCompany)).sort();

  const columns = useMemo(() => getColumns(type), [type])

  const filtered = useMemo(() => contracts.filter(filterByTerm(term)).filter(filterByParentCompany(parentCompany)), [term, contracts, parentCompany])

  const paged = useMemo(() => filtered.slice(page.size * page.page, page.size * page.page + page.size), [filtered, page]);

  return (
    <ListPageWrapper>
      <PageTitle title="Plan coverage">
        <Stack gap={2} direction={'row'} flex={1}>
          <Tabs value={tab} onChange={(e, value) => setTab(value)} sx={{flex: 1, ml: 5}}>
            <MuiTab label="Medicare Advantage" />
            <MuiTab label="Prescription Drug" />
          </Tabs>
          <SearchInput value={term}
                       wrapperSx={{width: 'initial'}}
                       onChange={setTerm} />
          <ParentCompanyFilter parentCompanies={parentCompanies} onChange={setParentCompany} />
        </Stack>


      </PageTitle>

      <DataGrid columns={columns}
                page={page.page}
                rowsPerPage={page.size}
                total={filtered.length || 0}
                loading={loading}
                onPageChange={p => setPage(prev => ({...prev, page: p }))}
                onRowsPerPageChange={p => setPage(prev => ({...prev, size: p }))}
                rows={paged}/>
    </ListPageWrapper>

  );
};

type Contract = Omit<ReturnType<typeof usePlanContracts>['contracts'][0], '__typename'>

const getColumns = (type: 'pdp' | 'ma'): DataGridColumn<Contract, null>[] => [
  {
    title: 'Contract',
    value: v => v.contract,
  },
  {
    title: 'Parent Company',
    value: v => v.parentCompany,
  },
  {
    title: 'Company',
    value: v => v.company,
  },
  {
    title: 'Enabled',
    value: v => <EnabledToggle item={v} type={type} />
  },
]

const EnabledToggle = ({item, type}: {item: Contract, type: 'pdp' | 'ma'}) => {
  const options = {
    client: mainBackendApolloClient,
    onCompleted: () => {
      enqueueSnackbar('Changes saved!', { variant: 'success' })
    },
    refetchQueries: [`enabled${_.startCase(type)}Contracts`]
  }
  const [saveMa, {loading: saveMaLoading}] = useSaveEnabledMaContractMutation(options);
  const [savePdp, {loading: savePdpLoading}] = useSaveEnabledPdpContractMutation(options);

  const handleClick = () => {
    const save = type === 'ma' ? saveMa : savePdp;
    save({variables: {input: {...item, enabled: !item.enabled} }})
  }

  const loading = saveMaLoading || savePdpLoading;

  return (
    loading ? <CircularProgress size={14} /> :
      <IconButton size={'small'} onClick={handleClick}>
        {item.enabled ?
          <Check weight={'bold'} color={'green'} size={14} /> :
          <X weight={'bold'} color={'red'} size={14} />}
      </IconButton>
  )
}


const filterByTerm = (term: string) => (contract: {contract: string, company: string, parentCompany: string}) => (
  [contract.contract.toLowerCase(), contract.company.toLowerCase(), contract.parentCompany.toLowerCase()].some(t => t.indexOf(term.toLowerCase()) >= 0)
)

const filterByParentCompany = (parentCompany: string | null) => (contract: {parentCompany: string}) => (
  parentCompany ? contract.parentCompany === parentCompany : true
)

const useParentCompany = (suffix: string) => {
  return useSessionStorageState<string | null>(
    'planCoverageParentCompany' + suffix,
    ''
  );
};

const useTerm = (suffix: string) => {
  return useSessionStorageState<string>(
    'planCoverageTerm' + suffix,
    ''
  );
};

const usePage = (suffix: string) => {
  return useSessionStorageState<PageInput>(
    'planCoveragePage' + suffix,
    { page: 0, size: 20 }
  );
};
